import { TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { ElementData } from "../Data/ElementData";
import { RegionData } from "../Data/RegionData";
import { Regions, RegionType } from "../Data/Regions";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOverKeys";
import { Balloon } from "../GameObjects/Balloon";
import { ParallaxLayer } from "../GameObjects/ParallaxLayer";
import { TicketBooth } from "../GameObjects/TicketBooth";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";
import { WorldElement } from "../GameObjects/WorldElement";

interface ICheckpoint {
    element: ElementData;
    pos: number;
}

export class GameplayLayer extends ParallaxLayer {
    private static readonly START: ElementData = new ElementData("START");
    private static readonly TICKET: ElementData = new ElementData("TICKET");
    private static readonly FINISH: ElementData = new ElementData("FINISH");
    private static readonly PRE_TICKET: ElementData = new ElementData("PRE_TICKET");
    private static readonly POST_TICKET: ElementData = new ElementData("POST_TICKET");
    private static readonly FUN_FACT: ElementData = new ElementData(
        "FUN FACT", undefined, false, false, undefined, Constants.FUN_FACT_GENERIC_PLACEHOLDER,
    );
    private static readonly DECORATOR_RANGE: PIXI.Point = new PIXI.Point(-75, 600);

    private _regionData: RegionData;
    private _decorators: ReadonlyArray<ElementData>;
    private _generic: ReadonlyArray<ElementData>;
    private _unique: ReadonlyArray<ElementData>;
    private _prevGeneric: ElementData | undefined;
    private _prevDecorator: ElementData | undefined;
    private _checkpoints: ICheckpoint[];
    private _distPerc: number;
    private _spawnOffset: number;
    private _checkpointIndex: number;
    private _decoratorDist: number;
    private _funFactDelay: TimelineLite | undefined;
    private _genericFunFacts: string[];

    constructor() {
        super(Constants.PARALLAX_SPEED, [], new PIXI.Point(), new PIXI.Point(), new PIXI.Point());

        this._distPerc = 0;
        this._spawnOffset = 0;
        this.isSpawning = false;
        this._regionData = Regions.activeData;
        this._decorators = this._regionData.decorators;
        this._generic = this._regionData.generic;
        const unique: ElementData[] = new Array<ElementData>();
        for (let i = 0; i < this._regionData.unique.length; ++i) {
            unique.push(this._regionData.unique[i]);
        }
        HLF.ArrayUtils.shuffle(unique);
        this._unique = unique;

        this._decoratorDist = HLF.Random.floatBetweenPoint(GameplayLayer.DECORATOR_RANGE);

        this._genericFunFacts = new Array<string>();
        const regionIndex: number = AppProgress.activeLevel;
        const factCount: number = VoiceOver.FF_GEN_COUNT[regionIndex];
        for (let i = 0; i < factCount; ++i) {
            this._genericFunFacts.push(VoiceOver.FF_GEN_PREFIX + VoiceOver.REGION_SUFFIX[regionIndex] + "-0" + i);
        }
        HLF.ArrayUtils.shuffle(this._genericFunFacts);

        this._checkpointIndex = 0;
        this._checkpoints = new Array<ICheckpoint>();
        this.generateCheckpoints();
        this.sortCheckpoints();

        if (AppProgress.checkpointReached) {
            this._distPerc = 0.49;
            while (this.activeCheckpoint && this.activeCheckpoint.pos < this._distPerc) {
                this._checkpointIndex++;
            }
        }
    }

    private get activeCheckpoint(): ICheckpoint | undefined {
        return this._checkpointIndex < this._checkpoints.length ? this._checkpoints[this._checkpointIndex] : undefined;
    }

    public init(pSize: PIXI.Point): void {
        this._size.copy(pSize);
        this.spawnPrepopulated();
        this.isSpawning = true;
        this._spawnOffset = (pSize.x * 0.5 - Balloon.DEFAULT_X) / Constants.LEVEL_SIZE;
        // this._spawnOffset = ((pSize.x - 1024) / Constants.LEVEL_SIZE) / 2;
    }

    public update(pDeltaTime: number): void {
        const deltaX: number = pDeltaTime * this._speed * this._speedMod;
        this._distPerc += Math.abs(deltaX) / Constants.LEVEL_SIZE;
        this._decoratorDist -= Math.abs(deltaX);

        if (this.rightElement === undefined || this.rightElement.rightEdge < this._size.x * 0.5) {
            if (this.activeCheckpoint && (this._distPerc + this._spawnOffset) >= this.activeCheckpoint.pos) {
                this.spawnElement(this.activeCheckpoint.element);
                this.playElementFunFact(this.activeCheckpoint.element);
                this._checkpointIndex++;
                this._decoratorDist =
                    this.rightElement.width + HLF.Random.floatBetweenPoint(GameplayLayer.DECORATOR_RANGE);
            }
            else if (this.isSpawning && this._decoratorDist > 0) {
                let decorator: ElementData | undefined;
                while (decorator === undefined || decorator === this._prevDecorator) {
                    decorator = HLF.ArrayUtils.random(this._decorators as ElementData[]);
                }
                this._prevDecorator = decorator;
                this.spawnElement(decorator);
                this.rightElement.flipped = HLF.Random.bool();
                this.rightElement.imageScale = HLF.Random.floatBetween(0.7, 1.0);
            }
            else if (this._isSpawning) {
                let generic: ElementData | undefined;
                while (generic === undefined || generic === this._prevGeneric) {
                    generic = HLF.ArrayUtils.random(this._generic as ElementData[]);
                }
                this._prevGeneric = generic;
                this.spawnElement(generic);
                this._decoratorDist =
                    this.rightElement.width + HLF.Random.floatBetweenPoint(GameplayLayer.DECORATOR_RANGE);
            }
        }

        this.moveElements(deltaX);
        this.removeOffscreenElements();
        for (let i = 0; i < this._elements.length; ++i) {
            this._elements[i].update(pDeltaTime);
        }
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        if (this._funFactDelay !== undefined) { this._funFactDelay.kill(); }
    }

    protected spawnElement(pData: ElementData): void {
        if (pData === GameplayLayer.START) {
            this.spawnStart();
        }
        else if (pData === GameplayLayer.PRE_TICKET) {
            PubSub.publishSync(Topics.STOP_POWERUPS, undefined);
        }
        else if (pData === GameplayLayer.TICKET) {
            this.spawnTicket(AppProgress.checkpointReached === false);
        }
        else if (pData === GameplayLayer.POST_TICKET) {
            PubSub.publishSync(Topics.RESUME_POWERUPS, undefined);
        }
        else if (pData === GameplayLayer.FINISH) {
            this.spawnFinish();
        }
        else if (pData === GameplayLayer.FUN_FACT) {
            // Do nothing, fun fact playing handled in Update loop
        }
        else {
            const element: WorldElement = this.getElement();
            element.init(pData);
            element.position.set(this.rightEdge + HLF.Random.floatBetween(0, 50), 300);
            this.addChild(element);
            element.updateTransform();
            this._elements.push(element);
        }
    }

    private spawnPrepopulated(): void {
        let right: number = this._size.x * 0.5;
        let data: ElementData | undefined;
        let element: WorldElement;
        while (right > -this._size.x * 0.5) {
            while (data === undefined || data === this._prevDecorator) {
                data = HLF.ArrayUtils.random(this._decorators as ElementData[]);
            }
            this._prevDecorator = data;
            element = this.getElement();
            element.init(data);
            element.position.set(
                right - element.width,
                300,
            );
            element.flipped = HLF.Random.bool();
            element.imageScale = HLF.Random.floatBetween(0.7, 1.0);
            this._elements.push(element);
            this.addChild(element);
            right = element.position.x - HLF.Random.floatBetween(15, 50);
        }
    }

    private spawnStart(): void {
        const flag: WorldElement = this.getElement();
        flag.init(Regions.activeData.flag);
        flag.position.set(
            this.rightEdge,
            300,
        );
        this._elements.push(flag);
        this.addChild(flag);
    }

    private spawnTicket(pWithTicket: boolean): void {
        const ticketBooth: TicketBooth = new TicketBooth(pWithTicket);
        ticketBooth.position.set(this.rightEdge, 300);
        this._elements.push(ticketBooth);
        this.addChild(ticketBooth);
    }

    private spawnFinish(): void {
        if (AppProgress.activeLevel === 7) {
            const tent: WorldElement = this.getElement();
            tent.init(Regions.data[RegionType.JAPAN].extra![0]);
            tent.position.set(this.rightEdge, 300);
            this._elements.push(tent);
            this.addChild(tent);
        }
        else {
            let width: number = 250;

            const waterTransition: WorldElement = this.getElement();
            waterTransition.init(new ElementData("water-transition", "terrain-common", false));
            waterTransition.position.set(this.rightEdge + width, this._size.y * 0.5);
            this._elements.push(waterTransition);
            this.addChild(waterTransition);
            width += waterTransition.width;

            const water: WorldElement[] = new Array<WorldElement>(2);
            for (let i = 0; i < water.length; ++i) {
                water[i] = this.getElement();
                water[i].init(new ElementData("water", "terrain-common", false));
                water[i].position.set(this.rightEdge + width, this._size.y * 0.5);
                this.addChild(water[i]);
                this._elements.push(water[i]);
                width += water[i].width;
            }
        }
    }

    private generateCheckpoints(): void {
        this._checkpoints.push({
            element: GameplayLayer.START,
            pos: 0.01,
        });
        this._checkpoints.push({
            element: GameplayLayer.PRE_TICKET,
            pos: 0.45,
        });
        this._checkpoints.push({
            element: GameplayLayer.TICKET,
            pos: 0.5,
        });
        this._checkpoints.push({
            element: GameplayLayer.POST_TICKET,
            pos: 0.55,
        });
        this._checkpoints.push({
            element: GameplayLayer.FINISH,
            pos: 1.0,
        });

        const range: PIXI.Point = new PIXI.Point();
        let perc: number;

        const firstHalf: ElementData[] = new Array<ElementData>();
        firstHalf.push(...this._unique.slice(0, Math.floor(this._unique.length / 2)));
        while (firstHalf.length < 2) {
            firstHalf.push(GameplayLayer.FUN_FACT);
        }
        HLF.ArrayUtils.shuffle(firstHalf);
        const secondHalf: ElementData[] = new Array<ElementData>();
        secondHalf.push(...this._unique.slice(Math.floor(this._unique.length / 2), this._unique.length));
        while (secondHalf.length < 3) {
            secondHalf.push(GameplayLayer.FUN_FACT);
        }
        HLF.ArrayUtils.shuffle(secondHalf);

        // Spawns for the first half of the level
        range.set(0.2, 0.4);
        for (let i = 0; i < firstHalf.length; ++i) {
            perc = i / (firstHalf.length - 1);
            this._checkpoints.push({
                element: firstHalf[i],
                pos: range.x + perc * (range.y - range.x),
            });
        }

        // Spawns for the second half of the level
        range.set(0.65, 0.9);
        for (let i = 0; i < secondHalf.length; ++i) {
            perc = i / (secondHalf.length - 1);
            this._checkpoints.push({
                element: secondHalf[i],
                pos: range.x + perc * (range.y - range.x),
            });
        }
    }

    private sortCheckpoints(): void {
        let temp: ICheckpoint;
        for (let i = 0; i < this._checkpoints.length - 1; ++i) {
            for (let j = 0; j < this._checkpoints.length - i - 1; ++j) {
                if (this._checkpoints[j].pos > this._checkpoints[j + 1].pos) {
                    temp = this._checkpoints[j];
                    this._checkpoints[j] = this._checkpoints[j + 1];
                    this._checkpoints[j + 1] = temp;
                }
            }
        }
    }

    private playElementFunFact(pData: ElementData): void {
        let fact: string | undefined;
        if (pData.funFact !== undefined) {
            if (pData.funFact === Constants.FUN_FACT_GENERIC_PLACEHOLDER) {
                if (this._genericFunFacts.length > 0) {
                    fact = this._genericFunFacts.pop();
                }
            }
            else {
                fact = pData.funFact;
            }
        }
        if (fact !== undefined) {
            this._funFactDelay = new TimelineLite().call(() => {
                Application.instance.voiceOver.play(fact!, InteruptType.LESS, VoiceCategory.FUN_FACT);
            }, undefined, this, 1.0);
        }
    }
}
